/**
 * Single source of truth for public event URLs. The dynamic route is `app/event/[id]`
 * and the API resolves events by `Event.id` (see `GET /api/events/[id]`).
 */

/** Host “View as guest” opens `/event/:id?preview=1` — UI only; does not grant access. */
export const EVENT_GUEST_PREVIEW_QUERY_KEY = "preview" as const
export const EVENT_GUEST_PREVIEW_QUERY_VALUE = "1" as const

/**
 * Append guest-preview query for hosts opening the public route in a new tab.
 * Idempotent when the key is already present.
 */
export function appendGuestPreviewQuery(pathOrUrl: string): string {
  const key = EVENT_GUEST_PREVIEW_QUERY_KEY
  const value = EVENT_GUEST_PREVIEW_QUERY_VALUE
  if (
    pathOrUrl.includes(`?${key}=`) ||
    pathOrUrl.includes(`&${key}=`)
  ) {
    return pathOrUrl
  }
  const sep = pathOrUrl.includes("?") ? "&" : "?"
  return `${pathOrUrl}${sep}${key}=${value}`
}

export type EventPublicUrlLike = { id: string }

export function getEventPublicPath(eventLike: EventPublicUrlLike): string {
  const id = String(eventLike.id ?? "").trim()
  if (!id) return "/event"
  return `/event/${id}`
}

/**
 * Deep link to the event Photos tab, optionally a specific album media row (`Media.id`).
 * Used by in-app notifications; query keys match `app/event/[id]/page.tsx`.
 */
export function getEventPublicAlbumDeepLinkPath(
  eventLike: EventPublicUrlLike,
  mediaId?: string | null,
): string {
  const base = getEventPublicPath(eventLike)
  const mid = mediaId?.trim() ?? ""
  if (!mid) return `${base}?tab=photos`
  return `${base}?tab=photos&media=${encodeURIComponent(mid)}`
}

/**
 * Absolute URL when `origin` is set; otherwise same as {@link getEventPublicPath} (path only).
 */
export function getEventPublicUrl(
  eventLike: EventPublicUrlLike,
  origin?: string | null,
): string {
  const path = getEventPublicPath(eventLike)
  const o = origin?.trim().replace(/\/$/, "") ?? ""
  if (!o) return path
  return `${o}${path}`
}

/** Appends `?share=` / `&share=` for `link_only` access tokens. */
export function appendShareTokenQuery(
  pathOrAbsoluteUrl: string,
  shareToken: string | null | undefined,
): string {
  const t = shareToken?.trim() ?? ""
  if (!t) return pathOrAbsoluteUrl
  const sep = pathOrAbsoluteUrl.includes("?") ? "&" : "?"
  return `${pathOrAbsoluteUrl}${sep}share=${encodeURIComponent(t)}`
}
